<script>
import moment from "moment";
import ActivityForm from "./ActivityForm.vue";
import { PublishStatusEnum, publishStatusList, activityStatusList, findLabel, normalizeDict } from "../../help";
import {
  getTechActivityList,
  addTechActivity,
  updateTechActivity,
  publishTechActivity,
  cancelPublishTechActivity,
  getSectionDict,
  getActivityTypeDict,
} from "@/api/techInnoActivities";

export default {
  name: "ActivityList",
  components: { ActivityForm },
  data() {
    return {
      moment,
      PublishStatusEnum,
      publishStatusList,
      // 板块字典
      sectionList: [],
      // 活动类型字典
      activityTypeList: [],
      // 搜索条件
      searchForm: {
        // 活动名称
        name: "",
        // 板块id
        sectionId: undefined,
        // 活动类型id
        typeId: undefined,
      },
      // 时间（活动开始时间）
      activityTime: [],
      table: {
        columns: [
          {
            title: "名称",
            dataIndex: "name",
            key: "name",
            width: 180,
            scopedSlots: { customRender: "name" },
          },
          {
            title: "板块",
            dataIndex: "sectionName",
            key: "sectionName",
            width: 110,
          },
          {
            title: "活动类型",
            dataIndex: "typeName",
            key: "typeName",
            width: 110,
          },
          {
            title: "活动时间",
            key: "beginTime",
            width: 320,
            ellipsis: true,
            customRender: (text) =>
              text.beginTime ? `${text.beginTime} ~ ${text.endTime}` : "--",
          },
          {
            title: "地点",
            dataIndex: "place",
            key: "place",
            width: 150,
            ellipsis: true,
          },
          {
            title: "报名进度",
            key: "progress",
            width: 130,
            scopedSlots: { customRender: "progress" },
          },
          {
            title: "是否审核",
            dataIndex: "needAudit",
            key: "needAudit",
            width: 100,
            customRender: (text) => (text === 1 ? "是" : "否"),
          },
          {
            title: "是否签到",
            dataIndex: "needCheckin",
            key: "needCheckin",
            width: 100,
            customRender: (text) => (text === 1 ? "是" : "否"),
          },
          {
            title: "是否开放报名",
            dataIndex: "needRegistration",
            key: "needRegistration",
            width: 120,
            customRender: (text) => (text === 1 ? "是" : "否"),
          },
          {
            title: "发布状态",
            dataIndex: "publishStatus",
            key: "publishStatus",
            width: 100,
            customRender: (text) => findLabel(publishStatusList, text) || "--",
          },
          {
            title: "活动状态",
            dataIndex: "activityStatus",
            key: "activityStatus",
            width: 100,
            customRender: (text) => findLabel(activityStatusList, text) || "--",
          },
          {
            title: "操作",
            key: "action",
            width: 180,
            className: "action-ql-list",
            fixed: "right",
            scopedSlots: { customRender: "action" },
          },
        ],
        tableData: [],
        listLoading: false,
        pagination: {
          pageSizeOptions: ["10", "20", "30", "50", "100"],
          showSizeChanger: true,
          current: 1,
          pageSize: 10,
          total: 0,
          showQuickJumper: true,
          showTotal: (total) => `共 ${total} 条`,
        },
      },
      // 新增/编辑/查看弹窗
      formVisible: false,
      formMode: "add",
      currentActivity: {},
      // 发布/取消发布弹窗
      publishVisible: false,
      publishLoading: false,
      publishTarget: {},
      // true 发布 / false 取消发布
      publishIntent: true,
    };
  },
  created() {
    this.getDicts();
    this.getList();
  },
  mounted() {
    // 宽屏下表格无横向滚动，取消操作列固定，避免固定列 overlay 产生空白列
    this.handleResize = () => {
      const wide = this.$el.clientWidth >= 1790;
      const cols = this.table.columns;
      cols[cols.length - 1].fixed = wide ? "" : "right";
    };
    window.addEventListener("resize", this.handleResize);
    this.handleResize();
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
  },
  methods: {
    /**
     * @Description：板块、活动类型字典
     * @Author cxd
     * @Date 2026/10/26
     */
    async getDicts() {
      try {
        const [sectionRes, typeRes] = await Promise.all([getSectionDict(), getActivityTypeDict()]);
        if (String(sectionRes.code) === "200") {
          this.sectionList = normalizeDict(sectionRes.data);
        }
        if (String(typeRes.code) === "200") {
          this.activityTypeList = normalizeDict(typeRes.data);
        }
      } catch (e) {
        console.log(e);
      }
    },
    /**
     * @Description：活动列表（接口 page 从 1 开始）
     * @Author cxd
     * @Date 2026/10/26
     */
    async getList() {
      const params = {
        name: this.searchForm.name,
        sectionId: this.searchForm.sectionId,
        typeId: this.searchForm.typeId,
        page: this.table.pagination.current,
        size: this.table.pagination.pageSize,
      };
      if (this.activityTime.length) {
        params.beginTimeStart = moment(this.activityTime[0]).format("YYYY-MM-DD") + " 00:00:00";
        params.beginTimeEnd = moment(this.activityTime[1]).format("YYYY-MM-DD") + " 23:59:59";
      }
      this.table.listLoading = true;
      try {
        const res = await getTechActivityList(params);
        if (String(res.code) === "200") {
          this.table.tableData = (res.data && res.data.items) || [];
          this.table.pagination.total = (res.data && res.data.total) || 0;
        } else {
          this.$message.error(res.msg || "查询失败");
        }
      } catch (e) {
        this.$message.error("查询失败");
      } finally {
        this.table.listLoading = false;
      }
    },
    /**
     * @Description：查询
     * @Author cxd
     * @Date 2026/10/26
     */
    search() {
      this.table.pagination.current = 1;
      this.getList();
    },
    /**
     * @Description：重置
     * @Author cxd
     * @Date 2026/10/26
     */
    resetSearchForm() {
      this.searchForm = { name: "", sectionId: "", typeId: "" };
      this.activityTime = [];
      this.table.pagination.current = 1;
      this.table.pagination.pageSize = 10;
      this.getList();
    },
    /**
     * @Description：分页
     * @Author cxd
     * @Date 2026/10/26
     */
    pageChange(pagination) {
      this.table.pagination.current = pagination.current;
      this.table.pagination.pageSize = pagination.pageSize;
      this.getList();
    },
    /**
     * @Description：新增/编辑/查看
     * @Author cxd
     * @Date 2026/10/26
     */
    openForm(mode, record) {
      this.formMode = mode;
      this.currentActivity = record || {};
      this.formVisible = true;
    },
    /**
     * @Description：新增/编辑提交
     * @Author cxd
     * @Date 2026/10/26
     */
    async handleFormSubmit(params) {
      try {
        this.$refs.activityForm.confirmLoading = true;
        const res =
          this.formMode === "edit"
            ? await updateTechActivity(params)
            : await addTechActivity(params);
        if (String(res.code) === "200") {
          this.$message.success(res.msg || "操作成功");
          this.formVisible = false;
          this.getList();
        } else {
          this.$message.error(res.msg || "操作失败");
        }
      } catch (e) {
        this.$message.error("操作失败");
      } finally {
        this.$refs.activityForm.confirmLoading = false;
      }
    },
    /**
     * @Description：发布/取消发布
     * @Author cxd
     * @Date 2026/10/26
     */
    handlePublish(record, intent) {
      this.publishTarget = record;
      this.publishIntent = intent;
      this.publishVisible = true;
    },
    /**
     * @Description：发布/取消发布确认
     * @Author cxd
     * @Date 2026/10/26
     */
    async confirmPublish() {
      this.publishLoading = true;
      try {
        const res = this.publishIntent
          ? await publishTechActivity(this.publishTarget.id)
          : await cancelPublishTechActivity(this.publishTarget.id);
        if (String(res.code) === "200") {
          this.$message.success(res.msg || "操作成功");
          this.publishVisible = false;
          this.getList();
        } else {
          this.$message.error(res.msg || "操作失败");
        }
      } catch (e) {
        this.$message.error("操作失败");
      } finally {
        this.publishLoading = false;
      }
    },
  },
};
</script>

<template>
  <div class="activity-list">
    <a-form layout="inline" style="margin-bottom: 10px">
      <a-form-item label="活动名称：">
        <a-input
          v-model.trim="searchForm.name"
          placeholder="请输入活动名称搜索"
          style="width: 200px"
        />
      </a-form-item>
      <a-form-item label="时间：">
        <a-range-picker
          v-model="activityTime"
          :placeholder="['开始时间', '结束时间']"
          format="YYYY-MM-DD"
          :getCalendarContainer="(triggerNode) => triggerNode.parentNode"
        />
      </a-form-item>
      <a-form-item label="板块：">
        <a-select
          v-model="searchForm.sectionId"
          placeholder="请选择板块"
          style="width: 160px"
          :getPopupContainer="(triggerNode) => triggerNode.parentNode"
        >
          <a-select-option v-for="item in sectionList" :key="item.value" :value="item.value">
            {{ item.label }}
          </a-select-option>
        </a-select>
      </a-form-item>
      <a-form-item label="活动类型：">
        <a-select
          v-model="searchForm.typeId"
          placeholder="请选择活动类型"
          style="width: 160px"
          :getPopupContainer="(triggerNode) => triggerNode.parentNode"
        >
          <a-select-option
            v-for="item in activityTypeList"
            :key="item.value"
            :value="item.value"
          >
            {{ item.label }}
          </a-select-option>
        </a-select>
      </a-form-item>
      <span>
        <a-button type="primary" style="margin-top: 3px" icon="search" @click="search">
          查询
        </a-button>
        <a-button style="margin-left: 15px" icon="redo" @click="resetSearchForm">重置</a-button>
      </span>
    </a-form>
    <div style="margin: 10px 0">
      <a-button type="primary" @click="openForm('add')">
        <a-icon type="plus" /> 新增活动
      </a-button>
    </div>
    <a-table
      :rowKey="(record, index) => index"
      :columns="table.columns"
      :data-source="table.tableData"
      :loading="table.listLoading"
      :pagination="table.pagination.total ? table.pagination : false"
      :scroll="{ x: 1750 }"
      bordered
      @change="pageChange"
    >
      <span slot="name" slot-scope="text, record">
        <a-tooltip>
          <template slot="title">
            <span>{{ record.name }}</span>
          </template>
          <div class="line">
            {{ record.name }}
          </div>
        </a-tooltip>
      </span>
      <span slot="progress" slot-scope="text, record">
        <template v-if="record.capacity != null">
          <span>{{ record.occupied != null ? record.occupied : 0 }}/{{ record.capacity }}</span>
          <a-tag
            v-if="record.capacity > 0 && (record.occupied || 0) >= record.capacity"
            color="red"
            class="full-tag"
          >
            已满
          </a-tag>
        </template>
        <span v-else>--</span>
      </span>
      <span slot="action" slot-scope="text, record" class="action">
        <a-button
          v-if="record.publishStatus === PublishStatusEnum.NOT_PUBLISH"
          type="link"
          class="btn"
          @click="openForm('edit', record)"
        >
          编辑
        </a-button>
        <a-button type="link" class="btn" @click="openForm('view', record)"> 查看 </a-button>
        <a-button
          v-if="record.publishStatus === PublishStatusEnum.NOT_PUBLISH"
          type="link"
          class="btn"
          @click="handlePublish(record, true)"
        >
          发布
        </a-button>
        <a-button
          v-if="record.publishStatus === PublishStatusEnum.ALREADY_PUBLISH"
          type="link"
          class="btn"
          @click="handlePublish(record, false)"
        >
          取消发布
        </a-button>
      </span>
    </a-table>
    <!-- 发布/取消发布确认 -->
    <a-modal
      :title="publishIntent ? '发布' : '取消发布'"
      :visible="publishVisible"
      :confirm-loading="publishLoading"
      @ok="confirmPublish"
      @cancel="publishVisible = false"
    >
      <p v-if="publishIntent" style="font-weight: 600; text-align: center">
        活动发布后，用户将可查看该活动，确定要发布此活动吗？
      </p>
      <p v-else style="font-weight: 600; text-align: center">
        活动取消发布后，用户将无法查看该活动，已产生的报名记录将保留，确定要取消发布此活动吗？
      </p>
    </a-modal>
    <!-- 新增/编辑/查看 -->
    <activity-form
      ref="activityForm"
      :visible="formVisible"
      :mode="formMode"
      :activity-data="currentActivity"
      :section-list="sectionList"
      :activity-type-list="activityTypeList"
      @close="formVisible = false"
      @submit="handleFormSubmit"
    />
  </div>
</template>

<style scoped lang="scss">
.action {
  // 操作按钮不允许换行，避免窄列下串行
  white-space: nowrap;
}

.full-tag {
  margin-left: 4px;
  line-height: 16px;
}

.line {
  // 单行省略，保证固定列与主表格行高一致，避免错位
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.btn {
  padding: 0;
  margin-left: 5px;
}
</style>
